// Phase 4 — Genomic Selection (rrBLUP / GBLUP / rrBLUP+Fixed-QTL).
//
// Niranjan (Feb 2026) — predicts breeding values (GEBVs) from a
// numeric genotype matrix + training phenotypes. BATCH-NEXT-2
// adds rrBLUP+Fixed-QTL (Bernardo 2014) as a third model with
// optional Phase-2 trait-architecture bridge for picking which
// markers become fixed effects above a PVE threshold.
//
// `Phase4ResultPanels` is exported so /results/<id> renders the
// `molecular_genomic_selection` section as a proper view instead
// of falling through to the generic key-value renderer.
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle2, ExternalLink, Download, FileText, Trophy } from 'lucide-react';
import { API } from '@/App';
import { sha256OfFile, recordAnalysis, getRegistry } from '@/utils/molecularSessionRegistry';
import { StepHeader, ParamLabel, RuntimeEstimate, useCsvHeader, autoSelectColumn, estimateRuntime } from './uploadCardHelpers';
import { HeritabilityGatingBanner, getPhase1TraitInfo } from './heritabilityGating';

const TIPS_P4 = {
  genotype_col: "Column in the phenotype file that holds the genotype/line ID. Must match the row index of the genotype file.",
  n_folds: "Cross-validation folds. 5 is standard. More folds give a smaller test set per iteration but more iterations.",
  n_reps: "Repetitions of the full CV procedure with different random splits. 10 is sufficient for stable accuracy estimates.",
  apply_marker_qc: "Apply Phase 1 marker QC (MAF / missingness / heterozygosity) before fitting rrBLUP and GBLUP.",
  model_choice: "rrBLUP (marker effects) / GBLUP (kinship-based) / rrBLUP+Fixed-QTL (major QTL as fixed effects, Bernardo 2014) / Compare all three (paired Wilcoxon recommendation).",
  pve_threshold: "Minimum PVE percent for a Phase 2 QTL to be promoted to a fixed effect. 5% = Bernardo (2014) default for crops. 10% = stricter (maize literature). 1% = liberal (small-effect inclusion). Higher thresholds reduce fixed-effect set.",
};


const MODEL_OPTIONS = [
  { value: 'both', label: 'rrBLUP + GBLUP (default)' },
  { value: 'rrblup', label: 'rrBLUP only' },
  { value: 'gblup', label: 'GBLUP only' },
  { value: 'rrblup_fixed_qtl', label: 'rrBLUP + Fixed-QTL (Bernardo 2014)' },
  { value: 'compare_all', label: 'Compare all three (Wilcoxon paired test)' },
];


function modelNeedsArchitecture(m) {
  return m === 'rrblup_fixed_qtl' || m === 'compare_all';
}


export function Phase4UploadCard({ onResult, analysisId, exporting, onExport }) {
  const [genoFile, setGenoFile] = useState(null);
  const [phenoFile, setPhenoFile] = useState(null);
  const [genotypeCol, setGenotypeCol] = useState('Genotype');
  const [nFolds, setNFolds] = useState(5);
  const [nReps, setNReps] = useState(10);
  const [applyQc, setApplyQc] = useState(true);
  const [modelChoice, setModelChoice] = useState('both');
  const [pveThreshold, setPveThreshold] = useState(5);
  const [running, setRunning] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [result, setResult] = useState(null);

  // Phase 1 (heritability) + Phase 2 (architecture) bridge state
  const [phase1Summary, setPhase1Summary] = useState(null);
  const [phase2Architecture, setPhase2Architecture] = useState(null);
  const [proceedAnyway, setProceedAnyway] = useState(false);

  // BATCH-NEXT-3 (Niranjan, Feb 2026): "Promote to Fixed-QTL"
  // handoff banner — read sessionStorage on mount.
  const [promotePayload, setPromotePayload] = useState(null);
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem('bk_phase4_promote');
      if (!raw) return;
      const p = JSON.parse(raw);
      if (!p || !p.auto_promoted || !p.trait_architecture) return;
      // Stale check — ignore promote payloads older than 30 minutes
      if (Date.now() - (p.timestamp || 0) > 30 * 60 * 1000) {
        sessionStorage.removeItem('bk_phase4_promote');
        return;
      }
      setPromotePayload(p);
      setPhase2Architecture(p.trait_architecture);
      setModelChoice('rrblup_fixed_qtl');
      const rec = Math.max(1, Math.min(20, Math.round(p.recommended_pve_threshold || 5)));
      setPveThreshold(rec);
    } catch { /* ignore corrupt payload */ }
  }, []);

  const resetPromote = () => {
    try { sessionStorage.removeItem('bk_phase4_promote'); } catch { /* ignore */ }
    setPromotePayload(null);
    setPhase2Architecture(null);
    setModelChoice('both');
    setPveThreshold(5);
  };

  const onPickGeno = (e) => { const f = e.target.files?.[0]; if (f) setGenoFile(f); };
  const onPickPheno = (e) => { const f = e.target.files?.[0]; if (f) setPhenoFile(f); };

  // When the genotype file changes, hash it and look up matching
  // Phase 1 + Phase 2 entries from the cross-phase registry.
  React.useEffect(() => {
    if (!genoFile) {
      setPhase1Summary(null);
      setPhase2Architecture(null);
      return;
    }
    let cancelled = false;
    sha256OfFile(genoFile).then(hash => {
      if (cancelled || !hash) return;
      const summary = getPhase1TraitInfo(hash);
      setPhase1Summary(summary);
      // Phase 2 architecture lookup
      const reg = getRegistry();
      let arch = null;
      for (const [, r] of Object.entries(reg)) {
        if (r.phase === 'qtl' && r.genotypeHash === hash && r.summary?.trait_architecture) {
          arch = r.summary.trait_architecture;
          break;
        }
      }
      setPhase2Architecture(arch);
    });
    return () => { cancelled = true; };
  }, [genoFile]);

  const loadSampleData = async () => {
    setLoadingSample(true);
    try {
      const [g, p] = await Promise.all([
        axios.get(`${API}/molecular/sample-data/genotype`, { responseType: 'blob', withCredentials: true }),
        axios.get(`${API}/molecular/sample-data/phenotype`, { responseType: 'blob', withCredentials: true }),
      ]);
      setGenoFile(new File([g.data], 'molecular_phase4_genotype.csv', { type: 'text/csv' }));
      setPhenoFile(new File([p.data], 'molecular_phase4_phenotype.csv', { type: 'text/csv' }));
      toast.success('Sample data loaded — 100 lines × 500 SNPs × 3 traits.');
    } catch (e) {
      toast.error('Sample data fetch failed.');
    } finally { setLoadingSample(false); }
  };

  const runAnalysis = async () => {
    if (!genoFile || !phenoFile) {
      toast.error('Both genotype and phenotype files are required.');
      return;
    }
    if (modelNeedsArchitecture(modelChoice) && !phase2Architecture) {
      const proceed = window.confirm(
        'No Phase 2 trait architecture in this session. The Fixed-QTL ' +
        'model will degenerate to plain rrBLUP for every trait. Run Phase 2 ' +
        'first (with the same genotype file) to get fixed-effect QTL ' +
        'candidates. Proceed anyway?'
      );
      if (!proceed) return;
    }
    setRunning(true);
    setResult(null);
    const fd = new FormData();
    fd.append('genotype_file', genoFile);
    fd.append('phenotype_file', phenoFile);
    fd.append('genotype_col', genotypeCol);
    fd.append('n_folds', String(nFolds));
    fd.append('n_reps', String(nReps));
    fd.append('apply_marker_qc', String(applyQc));
    fd.append('model_choice', modelChoice);
    fd.append('pve_threshold', String(pveThreshold));
    if (phase2Architecture) {
      fd.append('trait_architecture_json', JSON.stringify(phase2Architecture));
    }
    try {
      const { data } = await axios.post(`${API}/molecular/genomic-selection`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240000,
      });
      const r = data?.results?.molecular_genomic_selection;
      setResult(r);
      onResult(data);
      try {
        const genotypeHash = await sha256OfFile(genoFile);
        recordAnalysis(data.id, { phase: 'gs', genotypeHash });
      } catch { /* best-effort */ }
      toast.success('Genomic Selection complete!');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Run failed';
      toast.error(`Genomic Selection failed: ${msg}`);
    } finally { setRunning(false); }
  };

  const phenoHeaders = useCsvHeader(phenoFile);
  React.useEffect(() => {
    if (!phenoHeaders.length) return;
    if (!phenoHeaders.includes(genotypeCol)) {
      const guess = autoSelectColumn(phenoHeaders, ['^genotype$', '^line$', '^entry$', '^id$']);
      if (guess) setGenotypeCol(guess);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phenoHeaders.join(',')]);

  // Selected traits = numeric pheno cols (best-effort, not strict)
  const selectedTraits = phenoHeaders.filter(h => {
    const lower = h.toLowerCase();
    return !['rep', 'replication', 'replicate', 'block', genotypeCol.toLowerCase()].includes(lower);
  });
  const nIndividualsHint = phase1Summary?.nIndividuals || 100;
  const gatingActive = !!phase1Summary && !proceedAnyway;
  const gatingBlocks = gatingActive && (() => {
    if (!phase1Summary?.traits) return false;
    const threshold = (() => {
      const N = Math.max(1, nIndividualsHint || 1);
      return Math.min(0.40, Math.max(0.20, 0.20 + 100 / N));
    })();
    return selectedTraits.some(t => {
      const r = phase1Summary.traits[t];
      return r && typeof r.h2 === 'number' && r.h2 < threshold;
    });
  })();

  return (
    <div className="space-y-5" data-testid="mol-phase4-card">
      {promotePayload && (
        <div
          className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 flex items-start gap-3"
          data-testid="mol-phase4-promote-banner"
        >
          <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-xs leading-relaxed text-emerald-900">
            <div className="font-semibold mb-1">
              Imported {Object.keys(promotePayload.trait_architecture || {}).length} trait{Object.keys(promotePayload.trait_architecture || {}).length === 1 ? '' : 's'} of major QTL from Phase 2.
            </div>
            <div>
              Model pre-selected: <span className="font-mono font-semibold">rrBLUP + Fixed-QTL</span>.
              PVE threshold pre-set to <span className="font-mono font-semibold">{pveThreshold}%</span> (captures all detected major QTL).
              Adjust if needed before running.
            </div>
            <div className="text-[10px] text-emerald-700 mt-1.5">
              Eligible traits: {(promotePayload.eligible_traits || []).join(', ') || '—'}
            </div>
          </div>
          <button
            type="button"
            onClick={resetPromote}
            className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 underline shrink-0"
            data-testid="mol-phase4-promote-reset"
          >
            Reset to default
          </button>
        </div>
      )}

      {/* STEP 1 — UPLOAD */}
      <div>
        <StepHeader step="1" title="Upload your data" hint="Genotype + phenotype" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FileSlot label="Genotype file (0/1/2 numeric matrix)" file={genoFile} onPick={onPickGeno} testid="mol-phase4-genotype-file"
            accept=".csv,.xlsx,.xls" hint="Rows = individuals; Cols = SNPs / markers; Values in {0, 1, 2, NaN}." />
          <FileSlot label="Phenotype file" file={phenoFile} onPick={onPickPheno} testid="mol-phase4-phenotype-file"
            accept=".csv,.xlsx,.xls" hint="Cols: Genotype | Rep (optional) | Trait1 | Trait2 …" />
        </div>
      </div>

      {/* STEP 2 — CONFIGURE */}
      <div>
        <StepHeader step="2" title="Configure analysis" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <ColumnSelect label="Genotype column" value={genotypeCol} onChange={setGenotypeCol}
            options={phenoHeaders} testid="mol-phase4-genotype-col" hint={TIPS_P4.genotype_col} />
          <NumInput label="CV folds" value={nFolds} setValue={setNFolds} step={1} min={2} max={20}
            testid="mol-phase4-folds" hint={TIPS_P4.n_folds} />
          <NumInput label="CV repeats" value={nReps} setValue={setNReps} step={1} min={1} max={50}
            testid="mol-phase4-reps" hint={TIPS_P4.n_reps} />
          <ToggleInput label="Apply Phase 1 marker QC" value={applyQc} setValue={setApplyQc}
            testid="mol-phase4-apply-qc" hint={TIPS_P4.apply_marker_qc} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <SelectInput label="Model" value={modelChoice} onChange={setModelChoice}
            options={MODEL_OPTIONS} testid="mol-phase4-model-choice" hint={TIPS_P4.model_choice} />
          {modelNeedsArchitecture(modelChoice) && (
            <PveSlider value={pveThreshold} setValue={setPveThreshold} testid="mol-phase4-pve-threshold" hint={TIPS_P4.pve_threshold} />
          )}
        </div>

        {modelNeedsArchitecture(modelChoice) && (
          <div className="mt-2 text-[11px] rounded-md px-3 py-2"
            style={{
              background: phase2Architecture ? '#DCFCE7' : '#FEF3C7',
              border: `1px solid ${phase2Architecture ? '#16A34A40' : '#F59E0B40'}`,
              color: phase2Architecture ? '#14532D' : '#92400E',
            }}
            data-testid="mol-phase4-architecture-status">
            {phase2Architecture
              ? `Phase 2 architecture detected for ${Object.keys(phase2Architecture).length} trait(s) — major QTL with PVE ≥ ${pveThreshold}% will be fitted as fixed effects.`
              : 'No Phase 2 architecture in this session. Run Phase 2 with the same genotype to populate fixed-effect QTL candidates.'}
          </div>
        )}

        <HeritabilityGatingBanner
          phase="gs"
          selectedTraits={selectedTraits}
          phase1Summary={phase1Summary}
          nIndividualsHint={nIndividualsHint}
          proceedAnyway={proceedAnyway}
          onProceedAnyway={setProceedAnyway}
          testid="mol-phase4-h2-gating"
        />
      </div>

      {/* STEP 3 — RUN */}
      <div>
        <StepHeader step="3" title="Run analysis" />
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={loadSampleData} disabled={loadingSample || running}
            className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3.5 py-2 border border-[#7C3AED]/30 text-[#7C3AED] bg-[#7C3AED]/5 hover:bg-[#7C3AED]/10 disabled:opacity-50"
            data-testid="mol-phase4-sample-data">
            {loadingSample ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Try Sample Data
          </button>
          <button type="button" onClick={runAnalysis} disabled={running || !genoFile || !phenoFile || gatingBlocks}
            className="flex items-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2 bg-[#7C3AED] text-white hover:bg-[#6B2FCC] disabled:opacity-50 shadow-sm"
            data-testid="mol-phase4-run">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {running ? `Running ${nFolds}-fold × ${nReps} reps CV…` : 'Run Genomic Selection'}
          </button>
          <RuntimeEstimate testid="mol-phase4-runtime-estimate"
            estimate={estimateRuntime({ phase: 'gs', nIndiv: 100, nMarkers: 500, nReps })} />
        </div>
      </div>

      {result && (
        <div className="space-y-5 fade-in" data-testid="mol-phase4-result">
          <div className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
            style={{ background: '#7C3AED10', border: '1px solid #7C3AED30' }}
            data-testid="analysis-complete-banner">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 28, height: 28, background: '#7C3AED', color: 'white' }}>
                <CheckCircle2 size={16} />
              </div>
              <div className="min-w-0">
                <div className="truncate" style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED' }}>Genomic Selection complete</div>
                <div className="truncate" style={{ fontSize: 11, color: '#5E5A56' }}>Open the dedicated Results page for tables, scatter plots, and downloads.</div>
              </div>
            </div>
            {analysisId && (
              <button onClick={() => window.location.href = `/results/${analysisId}`}
                className="flex items-center gap-1.5 shrink-0 transition-all"
                style={{ fontSize: 13, fontWeight: 700, borderRadius: 8, padding: '8px 18px',
                  border: '1px solid #7C3AED', color: 'white', background: '#7C3AED',
                  boxShadow: '0 2px 6px #7C3AED40' }}
                data-testid="view-full-results">
                <ExternalLink size={13} /> View Full Results
              </button>
            )}
          </div>

          <Phase4ResultPanels result={result} />

          {analysisId && onExport && (
            <div className="flex items-center gap-2 flex-wrap pt-2" data-testid="export-bar">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Export</span>
              {['docx', 'xlsx', 'pdf'].map(fmt => (
                <button key={fmt} onClick={() => onExport(analysisId, fmt)} disabled={!!exporting}
                  className="flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-all border shadow-sm disabled:opacity-50 border-[#7C3AED]/30 text-[#7C3AED] bg-[#7C3AED]/5 hover:bg-[#7C3AED]/10"
                  data-testid={`export-${fmt}-button`}>
                  {exporting === `${analysisId}-${fmt}` ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function FileSlot({ label, file, onPick, testid, accept, hint }) {
  return (
    <label className="block rounded-xl border-2 border-dashed border-[#E8E6E1] hover:border-[#7C3AED] p-4 cursor-pointer transition-colors bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <FileText size={14} className="text-[#7C3AED]" />
        <span className="text-xs font-semibold text-slate-700">{label}</span>
      </div>
      <input type="file" accept={accept} onChange={onPick} className="hidden" />
      <div className="text-xs text-slate-500 mb-2">{hint}</div>
      <div className="flex items-center gap-2 mt-2">
        {file ? (
          <span className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-[#7C3AED]/10 text-[#7C3AED] font-mono truncate max-w-full">
            <CheckCircle2 size={11} /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
          </span>
        ) : (
          <span className="text-[11px] text-slate-400 italic">Click to select a CSV / XLSX file</span>
        )}
      </div>
    </label>
  );
}


function ColInput({ label, value, onChange, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <input id={testid} type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
        data-testid={testid} />
    </div>
  );
}


function ColumnSelect({ label, value, onChange, options, allowNone, testid, hint }) {
  if (!options || options.length === 0) {
    return <ColInput label={label} value={value} onChange={onChange} testid={testid} hint={hint} />;
  }
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <select id={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 bg-white"
        data-testid={testid}>
        {allowNone && <option value="">(none)</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        {value && !options.includes(value) && (
          <option value={value}>{value} (custom)</option>
        )}
      </select>
    </div>
  );
}


function NumInput({ label, value, setValue, step, min, max, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <input id={testid} type="number" value={value} onChange={(e) => setValue(parseInt(e.target.value, 10) || min)}
        step={step} min={min} max={max}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
        data-testid={testid} />
    </div>
  );
}


function ToggleInput({ label, value, setValue, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <button id={testid} type="button" onClick={() => setValue(!value)}
        className={`w-full text-sm border rounded-md px-2 py-1.5 text-left transition-colors ${value ? 'border-[#7C3AED] bg-[#7C3AED]/10 text-[#7C3AED] font-semibold' : 'border-slate-200 bg-white text-slate-500'}`}
        data-testid={testid}>
        {value ? '✓ Enabled' : '✗ Disabled'}
      </button>
    </div>
  );
}


function SelectInput({ label, value, onChange, options, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <select id={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 bg-white"
        data-testid={testid}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}


function PveSlider({ value, setValue, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={`PVE threshold (${value}%)`} hint={hint} testid={testid} htmlFor={testid} />
      <div className="flex items-center gap-2">
        <input id={testid} type="range" min={1} max={20} step={1} value={value}
          onChange={(e) => setValue(parseInt(e.target.value, 10))}
          className="flex-1 accent-[#7C3AED]"
          data-testid={testid} />
        <span className="text-xs font-mono w-10 text-right">{value}%</span>
      </div>
      <div className="text-[10px] text-slate-400 mt-1">5% = Bernardo (2014) default · 10% = stricter (maize) · 1% = liberal</div>
    </div>
  );
}


export function Phase4ResultPanels({ result }) {
  if (!result) return null;
  const summary = result.input_summary || {};
  const perTrait = result.per_trait_accuracy || {};
  const scatterCharts = result.scatter_charts || {};

  // Helper: best CV mean across whichever models actually ran
  const bestAcc = (r) => {
    const xs = ['rrblup_acc_mean', 'gblup_acc_mean', 'rrblup_fixed_qtl_acc_mean']
      .map(k => r[k]).filter(v => typeof v === 'number');
    return xs.length ? Math.max(...xs) : -Infinity;
  };
  const traitsValid = Object.entries(perTrait).filter(([, r]) => !r.error);
  const bestTrait = traitsValid.reduce((best, curr) => {
    return curr[1] && bestAcc(curr[1]) > (best ? bestAcc(best[1]) : -Infinity) ? curr : best;
  }, null);

  // Detect if any trait used the Fixed-QTL model — drives the
  // comparison table's column visibility.
  const hasFixedQtl = traitsValid.some(([, r]) => 'rrblup_fixed_qtl_acc_mean' in r);
  const hasRrblup = traitsValid.some(([, r]) => 'rrblup_acc_mean' in r);
  const hasGblup = traitsValid.some(([, r]) => 'gblup_acc_mean' in r);

  return (
    <div className="space-y-5">
      {result.interpretation && (
        <div className="rounded-xl border border-[#7C3AED]/30 bg-[#FAF5FF] px-4 py-3 text-sm text-slate-700 leading-relaxed" data-testid="mol-phase4-interpretation">
          {result.interpretation}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryCard title="Input" testid="mol-phase4-input-summary" rows={[
          ['Individuals', summary.n_individuals],
          ['Markers', summary.n_markers],
          ['Phenotype observations', summary.n_phenotype_observations],
          ['Trait columns', (summary.trait_cols || []).join(', ') || '—'],
          ['CV: folds × reps', `${summary.n_folds} × ${summary.n_reps}`],
          ['Marker QC applied', summary.marker_qc_applied ? 'Yes' : 'No'],
          ['Model', summary.model_choice || 'both'],
          ['PVE threshold', summary.pve_threshold_pct != null ? `${summary.pve_threshold_pct}%` : '—'],
          ['Phase 2 architecture', summary.trait_architecture_supplied ? 'Supplied' : 'Not supplied'],
        ]} />
        {bestTrait && (
          <div className="rounded-xl border border-[#7C3AED]/30 bg-[#FAF5FF] p-4" data-testid="mol-phase4-best-trait">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={14} className="text-[#7C3AED]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[#7C3AED]">Best-predicted trait</span>
            </div>
            <div className="text-base font-semibold text-slate-800 mb-1">{bestTrait[0]}</div>
            <div className="text-sm text-slate-600 mb-2">
              Recommended: <span className="font-mono font-bold text-[#7C3AED]">{bestTrait[1].recommended_model || bestTrait[1].best_model}</span>
            </div>
            <div className="text-xs text-slate-500 space-y-0.5">
              {hasRrblup && bestTrait[1].rrblup_acc_mean != null && (
                <div>rrBLUP: r = {bestTrait[1].rrblup_acc_mean} ± {bestTrait[1].rrblup_acc_sd}</div>
              )}
              {hasGblup && bestTrait[1].gblup_acc_mean != null && (
                <div>GBLUP: r = {bestTrait[1].gblup_acc_mean} ± {bestTrait[1].gblup_acc_sd}</div>
              )}
              {hasFixedQtl && bestTrait[1].rrblup_fixed_qtl_acc_mean != null && (
                <div>rrBLUP+Fixed-QTL: r = {bestTrait[1].rrblup_fixed_qtl_acc_mean} ± {bestTrait[1].rrblup_fixed_qtl_acc_sd}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {Object.keys(perTrait).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="mol-phase4-accuracy-table">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
            Prediction accuracy (Pearson r) — {summary.n_folds}-fold × {summary.n_reps}-rep cross-validation
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2">Trait</th>
                {hasRrblup && <th className="text-right px-4 py-2">rrBLUP r ± SD</th>}
                {hasGblup && <th className="text-right px-4 py-2">GBLUP r ± SD</th>}
                {hasFixedQtl && <th className="text-right px-4 py-2">rrBLUP+Fixed-QTL r ± SD</th>}
                <th className="text-left px-4 py-2">Recommended</th>
                <th className="text-right px-4 py-2">N obs</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(perTrait).map(([trait, r]) => (
                <tr key={trait} className="border-t border-slate-100" data-testid={`mol-phase4-row-${trait}`}>
                  <td className="px-4 py-2 font-mono text-xs">{trait}</td>
                  {r.error ? (
                    <td colSpan={4 + (hasFixedQtl ? 1 : 0)} className="px-4 py-2 text-amber-700 text-xs italic">{r.error}</td>
                  ) : (
                    <>
                      {hasRrblup && (
                        <td className="px-4 py-2 text-right tabular-nums">
                          {r.rrblup_acc_mean != null ? `${r.rrblup_acc_mean.toFixed(3)} ± ${r.rrblup_acc_sd.toFixed(3)}` : '—'}
                        </td>
                      )}
                      {hasGblup && (
                        <td className="px-4 py-2 text-right tabular-nums">
                          {r.gblup_acc_mean != null ? `${r.gblup_acc_mean.toFixed(3)} ± ${r.gblup_acc_sd.toFixed(3)}` : '—'}
                        </td>
                      )}
                      {hasFixedQtl && (
                        <td className="px-4 py-2 text-right tabular-nums">
                          {r.rrblup_fixed_qtl_acc_mean != null ? `${r.rrblup_fixed_qtl_acc_mean.toFixed(3)} ± ${r.rrblup_fixed_qtl_acc_sd.toFixed(3)}` : '—'}
                        </td>
                      )}
                      <td className="px-4 py-2 text-xs font-semibold">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full"
                          style={{
                            background: r.recommended_model && r.recommended_model !== r.best_model ? '#16A34A20' : '#7C3AED15',
                            color: r.recommended_model && r.recommended_model !== r.best_model ? '#15803D' : '#7C3AED',
                            border: r.recommended_model && r.recommended_model !== r.best_model ? '1px solid #16A34A60' : '1px solid #7C3AED40',
                          }}
                          data-testid={`mol-phase4-recommended-${trait}`}>
                          {r.recommended_model || r.best_model}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-xs">{r.n_observations}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* BATCH-NEXT-2: Per-trait Wilcoxon comparison + fixed-QTL list */}
      {traitsValid.some(([, r]) => (r.model_comparisons || []).length > 0) && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="mol-phase4-comparison-table">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
            Pairwise model comparison (Wilcoxon signed-rank test, paired across CV reps)
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2">Trait</th>
                <th className="text-left px-4 py-2">Comparison</th>
                <th className="text-right px-4 py-2">Median improvement</th>
                <th className="text-right px-4 py-2">Wilcoxon p</th>
                <th className="text-left px-4 py-2">Recommend?</th>
              </tr>
            </thead>
            <tbody>
              {traitsValid.map(([trait, r]) => (r.model_comparisons || []).map((c, i) => (
                <tr key={`${trait}-${i}`} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{trait}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className="text-slate-500">{c.baseline}</span>
                    <span className="text-slate-300 mx-1">→</span>
                    <span className="font-semibold">{c.candidate}</span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-xs">
                    {(c.median_improvement * 100).toFixed(1)}%
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-xs">
                    {c.p_value != null && isFinite(c.p_value) ? c.p_value.toExponential(2) : '—'}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {c.recommend ? (
                      <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold border border-emerald-300">
                        ✓ Yes
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fixed-QTL list per trait when available */}
      {traitsValid.some(([, r]) => (r.fixed_qtl_used || []).length > 0) && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="mol-phase4-fixed-qtl-list">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">
            Fixed-effect QTL used (PVE ≥ {summary.pve_threshold_pct}% from Phase 2)
          </div>
          <div className="space-y-1.5">
            {traitsValid.map(([trait, r]) => {
              const list = r.fixed_qtl_used || [];
              return (
                <div key={trait} className="text-xs">
                  <span className="font-mono font-semibold text-emerald-900">{trait}:</span>{' '}
                  {list.length === 0
                    ? <span className="text-slate-500 italic">no QTL met threshold (degenerated to plain rrBLUP)</span>
                    : list.map(q => `${q.marker} (${q.pve_percent.toFixed(1)}%)`).join(', ')}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {result.chart && (
        <ChartCard title="Prediction accuracy boxplot — across CV reps × folds" b64={result.chart} testid="mol-phase4-boxplot" />
      )}

      {/* BATCH-NEXT-4: Phase 2 → Phase 4 Evidence Trail */}
      <EvidenceTrailCard perTrait={perTrait} />

      {/* BATCH-NEXT-4: Click-to-select candidate parents from GEBVs */}
      <GebvSelectionPanel perTrait={perTrait} />

      {/* One scatter per trait (observed vs predicted GEBVs) */}
      {Object.entries(scatterCharts).map(([trait, b64]) => (
        <ChartCard key={trait} title={`Observed vs Predicted GEBVs — ${trait}`} b64={b64} testid={`mol-phase4-scatter-${trait}`} />
      ))}
    </div>
  );
}


function SummaryCard({ title, rows, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
        {title}
      </div>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v], i) => (
          <div key={i} className="px-4 py-1.5 grid grid-cols-2 text-sm">
            <dt className="text-slate-500 text-xs">{k}</dt>
            <dd className="text-right text-slate-800 tabular-nums font-mono text-xs">{v ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


function ChartCard({ title, b64, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
        {title}
      </div>
      <div className="p-4 flex justify-center">
        <img src={`data:image/png;base64,${b64}`} alt={title} className="max-w-full" />
      </div>
    </div>
  );
}


// ─────────── BATCH-NEXT-4 (Niranjan, Feb 2026) — Evidence Trail ──
//
// Renders the Phase 2 → Phase 4 evidence trail: for each trait that
// used Fixed-QTL as the recommended model, show every fixed-effect
// QTL with its Phase 2 sharpness/LD diagnostic + Phase 4 β coefficient,
// classified into 🟢 strong / 🟡 moderate / 🟠 weak evidence chips.
// Closes the scientific loop — user sees WHY each QTL was selected.

const EVIDENCE_CHIP = {
  strong:   { emoji: '🟢', label: 'Strong',   bg: '#DCFCE7', fg: '#15803D', border: '#16A34A60' },
  moderate: { emoji: '🟡', label: 'Moderate', bg: '#FEF3C7', fg: '#92400E', border: '#F59E0B40' },
  weak:     { emoji: '🟠', label: 'Weak',     bg: '#FFEDD5', fg: '#9A3412', border: '#EA580C40' },
};


function EvidenceTrailCard({ perTrait }) {
  const [pedExpanded, setPedExpanded] = React.useState(false);
  // Eligible traits: have ≥1 fixed-effect β
  const eligible = Object.entries(perTrait).filter(([, r]) => (r.fixed_qtl_betas || []).length > 0);
  if (!eligible.length) return null;

  return (
    <div className="rounded-xl border border-emerald-200 bg-white overflow-hidden" data-testid="mol-phase4-evidence-trail">
      <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 text-xs font-semibold uppercase tracking-wider text-emerald-800">
        Why these QTL helped: Phase 2 → Phase 4 evidence trail
      </div>
      <div className="px-4 py-3 text-xs text-slate-600 border-b border-slate-100 leading-relaxed">
        Each fixed-effect QTL below was identified in Phase 2 QTL mapping and contributed to the prediction-accuracy lift over vanilla rrBLUP.
        Strong-evidence QTL (sharp peak, low flanking LD) are most reliable for breeding decisions.
      </div>

      {eligible.map(([trait, r]) => {
        const betas = r.fixed_qtl_betas || [];
        const counts = betas.reduce((acc, b) => {
          acc[b.evidence_band] = (acc[b.evidence_band] || 0) + 1;
          return acc;
        }, {});
        const lift = (() => {
          const rrbl = r.rrblup_acc_mean;
          const fxd = r.rrblup_fixed_qtl_acc_mean;
          if (rrbl == null || fxd == null) return null;
          return ((fxd - rrbl) / Math.max(Math.abs(rrbl), 1e-9)) * 100;
        })();
        return (
          <div key={trait} className="border-t border-slate-100" data-testid={`mol-phase4-evidence-trait-${trait}`}>
            <div className="px-4 py-2 bg-slate-50/60 text-sm">
              <span className="font-semibold">{trait}</span>
              {lift != null && (
                <span className="ml-2 text-emerald-700 font-mono text-xs">
                  accuracy lift {lift > 0 ? '+' : ''}{lift.toFixed(1)}%
                </span>
              )}
              <span className="ml-2 text-slate-500 text-xs">
                (vanilla rrBLUP r={(r.rrblup_acc_mean ?? 0).toFixed(3)} → Fixed-QTL r={(r.rrblup_fixed_qtl_acc_mean ?? 0).toFixed(3)})
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50/30 text-slate-600 text-[11px]">
                  <tr>
                    <th className="text-left px-3 py-1.5">QTL marker</th>
                    <th className="text-left px-3 py-1.5">Chr</th>
                    <th className="text-right px-3 py-1.5">Pos cM</th>
                    <th className="text-right px-3 py-1.5">LOD</th>
                    <th className="text-right px-3 py-1.5">PVE %</th>
                    <th className="text-right px-3 py-1.5">Sharpness</th>
                    <th className="text-right px-3 py-1.5">Flank r²</th>
                    <th className="text-left px-3 py-1.5">Evidence</th>
                    <th className="text-right px-3 py-1.5">β (fixed)</th>
                  </tr>
                </thead>
                <tbody>
                  {betas.map((b, i) => {
                    const chip = EVIDENCE_CHIP[b.evidence_band] || EVIDENCE_CHIP.moderate;
                    return (
                      <tr key={i} className="border-t border-slate-100" data-testid={`mol-phase4-evidence-row-${b.marker}`}>
                        <td className="px-3 py-1.5 font-mono">{b.marker}</td>
                        <td className="px-3 py-1.5">{b.chromosome ?? '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{b.position_cM != null ? Number(b.position_cM).toFixed(1) : '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{b.lod_score != null ? Number(b.lod_score).toFixed(2) : '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{b.pve_phase2_percent != null ? Number(b.pve_phase2_percent).toFixed(1) : '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{b.sharpness_score != null ? Number(b.sharpness_score).toFixed(3) : '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{b.mean_ld_to_flanks != null ? Number(b.mean_ld_to_flanks).toFixed(3) : '—'}</td>
                        <td className="px-3 py-1.5">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border"
                            style={{ background: chip.bg, color: chip.fg, borderColor: chip.border }}>
                            {chip.emoji} {chip.label}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-mono">
                          {b.beta_fixed_effect != null ? Number(b.beta_fixed_effect).toFixed(3) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-[11px] text-slate-600">
              Of {betas.length} fixed-effect QTL: {counts.strong || 0} strong, {counts.moderate || 0} moderate, {counts.weak || 0} weak.
              {(counts.weak || 0) > 0 && (
                <span className="text-amber-700">
                  {' '}Accuracy may be partially attributable to weak-evidence QTL — validate in independent population before single-marker MAS.
                </span>
              )}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => setPedExpanded(p => !p)}
        className="w-full px-4 py-2 text-[11px] text-slate-600 hover:bg-slate-50 border-t border-slate-100 text-left flex items-center gap-1.5"
        data-testid="mol-phase4-evidence-pedagogical-toggle"
      >
        <span style={{ display: 'inline-block', transform: pedExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        <span className="font-semibold">Why does this matter for breeding decisions?</span>
      </button>
      {pedExpanded && (
        <div className="px-5 py-3 text-xs text-slate-700 bg-slate-50/40 leading-relaxed border-t border-slate-100" data-testid="mol-phase4-evidence-pedagogical">
          <p className="mb-2">
            When you use a QTL for marker-assisted selection (MAS), you are betting that the marker-trait association is real and stable across generations and environments.
            Strong-evidence QTL (sharp peak, low flanking LD) have well-resolved positions and are likely to track the causal locus. Weak-evidence QTL may be hitch-hikes that won't replicate.
          </p>
          <p className="mb-2">
            Genomic selection (rrBLUP+Fixed-QTL) is robust to weak QTL because the polygenic background absorbs noise. But if you want to deploy individual markers for MAS, prioritize strong-evidence QTL.
          </p>
          <p className="text-[11px] text-slate-500 italic">
            Reference: Bernardo, R. (2014). Genomewide selection when major genes are known. <em>Crop Science</em> 54(1): 68–75.
          </p>
        </div>
      )}
    </div>
  );
}


// ─────────── BATCH-NEXT-4 (Niranjan, Feb 2026) — Parent Selection ───
//
// Per-trait GEBV table with Top-N / Top-% buttons, sticky selection
// summary, CSV export, View Details modal showing fixed-effect QTL
// carrier status. Selection persists in sessionStorage.

function GebvSelectionPanel({ perTrait }) {
  const traitsWithGebvs = Object.entries(perTrait).filter(([, r]) => (r.gebvs || []).length > 0);
  const [selected, setSelected] = React.useState({});
  const [crossTrait, setCrossTrait] = React.useState(false);
  const [activeTrait, setActiveTrait] = React.useState(traitsWithGebvs[0]?.[0]);
  const [detailGenotype, setDetailGenotype] = React.useState(null);

  // Hydrate from sessionStorage on mount
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem('bk_phase4_selected_parents');
      if (raw) {
        const p = JSON.parse(raw);
        if (p && p.selected) setSelected(p.selected);
      }
    } catch { /* ignore */ }
  }, []);

  // Persist to sessionStorage on change
  React.useEffect(() => {
    try {
      sessionStorage.setItem('bk_phase4_selected_parents', JSON.stringify({
        selected, timestamp: Date.now(),
      }));
    } catch { /* ignore */ }
  }, [selected]);

  // Cross-trait intersection — must be defined BEFORE any early
  // return to satisfy React's rules-of-hooks.
  const crossTraitTop = React.useMemo(() => {
    if (!crossTrait || traitsWithGebvs.length < 2) return null;
    const PCT = 20;
    const sets = traitsWithGebvs.map(([, r]) => {
      const sorted = (r.gebvs || []).slice().sort((a, b) => a.rank - b.rank);
      const cutoff = Math.max(1, Math.ceil(sorted.length * PCT / 100));
      return new Set(sorted.slice(0, cutoff).map(g => g.genotype));
    });
    const intersect = [...sets[0]].filter(g => sets.every(s => s.has(g)));
    return { ids: intersect, pct: PCT };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossTrait, JSON.stringify(traitsWithGebvs.map(([t]) => t))]);

  if (!traitsWithGebvs.length) return null;
  const trait = activeTrait || traitsWithGebvs[0][0];
  const traitData = perTrait[trait] || {};
  const gebvs = (traitData.gebvs || []).slice().sort((a, b) => a.rank - b.rank);

  const toggle = (gid) => {
    setSelected(prev => ({ ...prev, [gid]: !prev[gid] }));
  };
  const selectTopN = (n) => {
    const top = gebvs.slice(0, n).map(g => g.genotype);
    setSelected(top.reduce((acc, gid) => ({ ...acc, [gid]: true }), {}));
  };
  const selectTopPct = (pct) => {
    const n = Math.max(1, Math.ceil(gebvs.length * pct / 100));
    selectTopN(n);
  };
  const clearSelection = () => setSelected({});
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const selectedGebvs = gebvs.filter(g => selected[g.genotype]);

  const exportCsv = () => {
    if (!selectedIds.length) return;
    const allTraits = traitsWithGebvs.map(([t]) => t);
    const headers = ['Genotype'];
    allTraits.forEach(t => {
      headers.push(`${t}_GEBV`);
      headers.push(`${t}_Rank`);
    });
    headers.push('Selection_Date', 'Notes');
    const lookup = {};
    traitsWithGebvs.forEach(([t, r]) => {
      lookup[t] = {};
      (r.gebvs || []).forEach(g => { lookup[t][g.genotype] = g; });
    });
    const date = new Date().toISOString().slice(0, 10);
    const rows = selectedIds.map(gid => {
      const row = [gid];
      allTraits.forEach(t => {
        const g = lookup[t][gid];
        row.push(g ? g.gebv.toFixed(3) : '');
        row.push(g ? g.rank : '');
      });
      row.push(date, '');
      return row.join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `BKAgriD_selected_parents_${date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="mol-phase4-gebv-panel">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          Genomic estimated breeding values (GEBV) — pick candidate parents
        </span>
        {traitsWithGebvs.length > 1 && (
          <label className="text-xs flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={crossTrait} onChange={e => setCrossTrait(e.target.checked)}
              className="accent-[#7C3AED]" data-testid="mol-phase4-cross-trait-toggle" />
            <span>Cross-trait view</span>
          </label>
        )}
      </div>
      {/* Trait tabs */}
      {traitsWithGebvs.length > 1 && (
        <div className="flex gap-1 px-4 pt-2 border-b border-slate-100 bg-white">
          {traitsWithGebvs.map(([t]) => (
            <button key={t} type="button" onClick={() => setActiveTrait(t)}
              className={`text-[11px] px-3 py-1.5 rounded-t-md font-mono transition-colors ${trait === t ? 'bg-[#7C3AED]/10 text-[#7C3AED] border-b-2 border-[#7C3AED]' : 'text-slate-500 hover:bg-slate-50'}`}
              data-testid={`mol-phase4-gebv-tab-${t}`}>{t}</button>
          ))}
        </div>
      )}
      {/* Action buttons */}
      <div className="flex gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50/30">
        <button type="button" onClick={() => selectTopN(10)}
          className="text-[11px] font-semibold rounded-md px-3 py-1.5 bg-[#7C3AED] text-white hover:bg-[#6B2FCC] disabled:opacity-50"
          disabled={gebvs.length < 1}
          data-testid="mol-phase4-select-top-10">Select Top 10</button>
        <button type="button" onClick={() => selectTopPct(5)}
          className="text-[11px] font-semibold rounded-md px-3 py-1.5 bg-[#7C3AED] text-white hover:bg-[#6B2FCC] disabled:opacity-50"
          disabled={gebvs.length < 1}
          data-testid="mol-phase4-select-top-pct">Select Top 5%</button>
        <button type="button" onClick={clearSelection}
          className="text-[11px] font-semibold rounded-md px-3 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-100"
          data-testid="mol-phase4-clear-selection">Clear Selection</button>
        <button type="button" onClick={exportCsv} disabled={!selectedIds.length}
          className="text-[11px] font-semibold rounded-md px-3 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 ml-auto flex items-center gap-1"
          data-testid="mol-phase4-export-parents-csv">
          <Download size={11} /> Export CSV
        </button>
      </div>

      {crossTrait && crossTraitTop && (
        <div className="px-4 py-2 bg-indigo-50 text-[11px] text-indigo-900 border-b border-indigo-100" data-testid="mol-phase4-cross-trait-summary">
          <span className="font-semibold">Cross-trait elite (top {crossTraitTop.pct}% across all traits):</span>{' '}
          {crossTraitTop.ids.length === 0
            ? <span className="italic text-slate-500">no genotypes rank in the top {crossTraitTop.pct}% on every trait</span>
            : (
              <span className="font-mono">{crossTraitTop.ids.join(', ')}</span>
            )}
        </div>
      )}

      {/* GEBV table */}
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-xs">
          <thead className="bg-slate-50/50 text-slate-600 text-[11px] sticky top-0">
            <tr>
              <th className="text-center px-2 py-1.5 w-8"></th>
              <th className="text-right px-3 py-1.5">#</th>
              <th className="text-left px-3 py-1.5">Genotype</th>
              <th className="text-right px-3 py-1.5">GEBV</th>
              <th className="text-right px-3 py-1.5">Pred. {trait}</th>
              <th className="text-right px-3 py-1.5">Pctile</th>
              <th className="text-center px-3 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {gebvs.map(g => {
              const isSelected = !!selected[g.genotype];
              return (
                <tr key={g.genotype} className={`border-t border-slate-100 ${isSelected ? 'bg-[#7C3AED]/5' : 'hover:bg-slate-50/40'}`}
                  data-testid={`mol-phase4-gebv-row-${g.genotype}`}>
                  <td className="text-center px-2 py-1.5">
                    <input type="checkbox" checked={isSelected} onChange={() => toggle(g.genotype)}
                      className="accent-[#7C3AED]"
                      data-testid={`mol-phase4-gebv-check-${g.genotype}`} />
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{g.rank}</td>
                  <td className="px-3 py-1.5 font-mono">{g.genotype}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{g.gebv.toFixed(3)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{g.predicted_phenotype.toFixed(3)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{g.percentile}</td>
                  <td className="text-center px-2 py-1.5">
                    <button type="button" onClick={() => setDetailGenotype(g.genotype)}
                      className="text-[10px] text-[#7C3AED] hover:underline" data-testid={`mol-phase4-gebv-details-${g.genotype}`}>
                      Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Selection summary */}
      {selectedIds.length > 0 && (
        <div className="px-4 py-2.5 bg-[#7C3AED]/5 border-t border-[#7C3AED]/20 text-xs flex items-center gap-3 flex-wrap" data-testid="mol-phase4-selection-summary">
          <span><strong>Selected: {selectedIds.length} candidates.</strong></span>
          <span className="text-slate-600">
            Mean GEBV: <span className="font-mono">{(selectedGebvs.reduce((s, g) => s + g.gebv, 0) / selectedGebvs.length).toFixed(3)}</span> ±{' '}
            <span className="font-mono">{
              (() => {
                const m = selectedGebvs.reduce((s, g) => s + g.gebv, 0) / selectedGebvs.length;
                const v = selectedGebvs.reduce((s, g) => s + (g.gebv - m) ** 2, 0) / Math.max(selectedGebvs.length - 1, 1);
                return Math.sqrt(v).toFixed(3);
              })()
            }</span>
          </span>
          <span className="text-slate-600">
            Range: [<span className="font-mono">{Math.min(...selectedGebvs.map(g => g.gebv)).toFixed(3)}</span> – <span className="font-mono">{Math.max(...selectedGebvs.map(g => g.gebv)).toFixed(3)}</span>]
          </span>
        </div>
      )}

      {/* Details modal */}
      {detailGenotype && (
        <GenotypeDetailsModal
          genotypeId={detailGenotype}
          activeTrait={trait}
          perTrait={perTrait}
          selectedIds={selectedIds}
          onClose={() => setDetailGenotype(null)}
        />
      )}
    </div>
  );
}


function GenotypeDetailsModal({ genotypeId, activeTrait, perTrait, selectedIds, onClose }) {
  // Aggregate GEBVs across traits for this genotype
  const acrossTraits = Object.entries(perTrait).map(([t, r]) => {
    const g = (r.gebvs || []).find(x => x.genotype === genotypeId);
    return { trait: t, gebv: g?.gebv, rank: g?.rank, percentile: g?.percentile, predicted: g?.predicted_phenotype };
  }).filter(x => x.gebv != null);

  // Pull fixed-effect QTL list from the active trait (we don't have
  // per-genotype dosage stored client-side, but β + marker + chip
  // status give breeders the carrier-relevance picture).
  const betas = (perTrait[activeTrait]?.fixed_qtl_betas) || [];

  // Cross prediction: ŷ = mean(GEBV_self, GEBV_partner) for each
  // *other* selected parent in the active trait.
  const otherSelected = selectedIds.filter(s => s !== genotypeId);
  const selfG = (perTrait[activeTrait]?.gebvs || []).find(x => x.genotype === genotypeId);
  const crossPredictions = otherSelected.map(partner => {
    const partnerG = (perTrait[activeTrait]?.gebvs || []).find(x => x.genotype === partner);
    if (!partnerG || !selfG) return null;
    return { partner, predicted: (selfG.gebv + partnerG.gebv) / 2 };
  }).filter(Boolean);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}
      data-testid="mol-phase4-details-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Candidate parent</div>
            <div className="text-lg font-mono font-semibold text-slate-800">{genotypeId}</div>
          </div>
          <button type="button" onClick={onClose}
            className="text-2xl leading-none text-slate-400 hover:text-slate-700"
            data-testid="mol-phase4-details-close">×</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">GEBV across traits</div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50/50 text-slate-500 text-[11px]">
                <tr>
                  <th className="text-left px-3 py-1">Trait</th>
                  <th className="text-right px-3 py-1">GEBV</th>
                  <th className="text-right px-3 py-1">Rank</th>
                  <th className="text-right px-3 py-1">Pctile</th>
                  <th className="text-right px-3 py-1">Pred. phenotype</th>
                </tr>
              </thead>
              <tbody>
                {acrossTraits.map(t => (
                  <tr key={t.trait} className="border-t border-slate-100">
                    <td className="px-3 py-1 font-mono">{t.trait}</td>
                    <td className="px-3 py-1 text-right tabular-nums font-semibold">{t.gebv.toFixed(3)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{t.rank}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{t.percentile}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{t.predicted.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {betas.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">Fixed-effect QTL on {activeTrait}</div>
              <div className="text-[11px] text-slate-500 mb-1.5 italic">
                These markers were used as fixed effects in the rrBLUP+Fixed-QTL model. Carrier status would be visualized here when the genotype matrix is available client-side.
              </div>
              <table className="w-full text-xs">
                <thead className="bg-slate-50/50 text-slate-500 text-[11px]">
                  <tr>
                    <th className="text-left px-3 py-1">Marker</th>
                    <th className="text-right px-3 py-1">β</th>
                    <th className="text-left px-3 py-1">Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {betas.map((b, i) => {
                    const chip = EVIDENCE_CHIP[b.evidence_band] || EVIDENCE_CHIP.moderate;
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1 font-mono">{b.marker}</td>
                        <td className="px-3 py-1 text-right tabular-nums">{b.beta_fixed_effect != null ? b.beta_fixed_effect.toFixed(3) : '—'}</td>
                        <td className="px-3 py-1">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border"
                            style={{ background: chip.bg, color: chip.fg, borderColor: chip.border }}>
                            {chip.emoji} {chip.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {crossPredictions.length > 0 && (
            <div data-testid="mol-phase4-cross-prediction">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">Cross prediction on {activeTrait}</div>
              <div className="text-[11px] text-slate-500 mb-1.5 italic">
                Predicted F1 GEBV when crossed with each other selected parent: ŷ_F1 = (GEBV_self + GEBV_partner) / 2
              </div>
              <table className="w-full text-xs">
                <thead className="bg-slate-50/50 text-slate-500 text-[11px]">
                  <tr>
                    <th className="text-left px-3 py-1">Partner</th>
                    <th className="text-right px-3 py-1">Predicted F1 GEBV</th>
                  </tr>
                </thead>
                <tbody>
                  {crossPredictions.map(c => (
                    <tr key={c.partner} className="border-t border-slate-100">
                      <td className="px-3 py-1 font-mono">{c.partner}</td>
                      <td className="px-3 py-1 text-right tabular-nums font-semibold">{c.predicted.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
